import { useState, useEffect, useCallback } from 'react';
import {
  getBOLLatest,
  getLoadboardLastKnownLocation,
  getLoadboardRuns,
  getLoadboardRunItems,
  getLoadboardRecommendations,
  gmailSync,
  downloadDocument,
  getGoogleAuthStartUrl,
} from '../services/api';

const ENCODING_ERROR_THRESHOLD = 3;
/** Backend sentinel when extraction is garbled; Re-sync with OCR triggers visual-first re-parse. */
const PENDING_OCR_CLEANUP = 'Pending OCR Cleanup';

/** Count characters outside printable ASCII (\\x20-\\x7E). */
function countNonPrintable(s: string): number {
  if (!s || typeof s !== 'string') return 0;
  const match = s.match(/[^\x20-\x7E]/g);
  return match ? match.length : 0;
}

/** Human-readable display. If >3 non-ASCII chars, return "Text Encoding Error". Pass through PENDING_OCR_CLEANUP. */
function sanitizeDisplay(s: string | undefined | null): string {
  if (s == null || typeof s !== 'string') return '';
  if (s === PENDING_OCR_CLEANUP) return PENDING_OCR_CLEANUP;
  if (countNonPrintable(s) > ENCODING_ERROR_THRESHOLD) return 'Text Encoding Error';
  return s.replace(/\uFFFD/g, '').replace(/[\x00-\x1F\x7F]/g, '').replace(/\s+/g, ' ').trim().slice(0, 80);
}

/** True if any loadboard item cell indicates encoding/OCR cleanup needed (show Re-sync with OCR). */
function needsOcrResync(items: Array<{ unit_type?: string; pickup_city?: string; pickup_state?: string; drop_city?: string; drop_state?: string; raw_row?: string }>): boolean {
  return items.some((item) => {
    const pickup = [item.pickup_city, item.pickup_state].filter(Boolean).join(', ');
    const drop = [item.drop_city, item.drop_state].filter(Boolean).join(', ');
    return (
      item.unit_type === PENDING_OCR_CLEANUP ||
      item.pickup_city === PENDING_OCR_CLEANUP ||
      item.drop_city === PENDING_OCR_CLEANUP ||
      item.raw_row === PENDING_OCR_CLEANUP ||
      countNonPrintable(item.unit_type || '') > ENCODING_ERROR_THRESHOLD ||
      countNonPrintable(pickup) > ENCODING_ERROR_THRESHOLD ||
      countNonPrintable(drop) > ENCODING_ERROR_THRESHOLD ||
      countNonPrintable(item.raw_row || '') > ENCODING_ERROR_THRESHOLD
    );
  });
}

/** True if string looks like a real city (not encoding garbage like "shop 3.08BIM"). */
function looksLikeValidCity(s: string | undefined | null): boolean {
  if (s == null || typeof s !== 'string') return false;
  const t = s.trim();
  if (t.length < 2 || t.length > 60) return false;
  if (/\d{2,}/.test(t)) return false;
  if (/[\d.]+\s*BIM|ICC_PROFILE|mntrRGB/i.test(t)) return false;
  return /^[A-Za-z\s\-\.',]+$/.test(t);
}
import {
  Truck,
  MapPin,
  FileText,
  Loader2,
  AlertCircle,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';

export default function Loadboard() {
  const [bols, setBols] = useState<Array<{
    id: string; filename: string; date: string; documentId?: string; fileUrl?: string; downloadUrl?: string; fileBacked: boolean;
    bol?: { to_name?: string; to_city?: string; to_state?: string; to_zip?: string; raw_to_block?: string };
  }>>([]);
  const [runs, setRuns] = useState<Array<{
    id: string; documentId: string; downloadUrl?: string; receivedAt?: string; createdAt: string; filename?: string; r2Key?: string;
  }>>([]);
  const [runItems, setRunItems] = useState<Record<string, Array<{
    id: string; unit_type?: string; pickup_city?: string; pickup_state?: string; drop_city?: string; drop_state?: string;
    miles?: number; notes?: string; raw_row?: string;
  }>>>({});
  const [recommendations, setRecommendations] = useState<Array<{
    id: string; pickup_city?: string; pickup_state?: string; drop_city?: string; drop_state?: string;
    miles?: number; notes?: string; reason: string; distance_miles?: number;
  }>>([]);
  const [destinations, setDestinations] = useState<Array<{ to_city?: string; to_state?: string }>>([]);
  const [lastKnownLocation, setLastKnownLocation] = useState<{ city?: string; state?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncToast, setSyncToast] = useState<string | null>(null);

  const fetchData = useCallback(async (cacheBust?: number) => {
    setLoading(true);
    setError(null);
    try {
      const [locRes, bolRes, runsRes] = await Promise.all([
        getLoadboardLastKnownLocation(cacheBust),
        getBOLLatest(2),
        getLoadboardRuns(10, cacheBust),
      ]);
      const near = locRes.ok && locRes.city && locRes.state ? { city: locRes.city, state: locRes.state } : undefined;
      if (locRes.ok && (locRes.city || locRes.state)) {
        setLastKnownLocation({ city: locRes.city, state: locRes.state });
      } else {
        setLastKnownLocation(null);
      }
      if (bolRes.ok) setBols(bolRes.bols);
      if (runsRes.ok) {
        setRuns(runsRes.runs);
        for (const run of runsRes.runs.slice(0, 1)) {
          const itemsRes = await getLoadboardRunItems(run.id, cacheBust);
          if (itemsRes.ok) setRunItems((prev) => ({ ...prev, [run.id]: itemsRes.items }));
        }
      }
      const recRes = await getLoadboardRecommendations(10, near, cacheBust);
      if (recRes.ok) {
        setRecommendations(recRes.recommendations || []);
        setDestinations(recRes.destinations || []);
        if (!near && recRes.last_known_location && (recRes.last_known_location.city || recRes.last_known_location.state)) {
          setLastKnownLocation(recRes.last_known_location);
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSyncLoadboard = async () => {
    setSyncing(true);
    setError(null);
    setSyncToast(null);
    setRuns([]);
    setRunItems({});
    try {
      const result = await gmailSync('.Loadboard', 30, 5);
      const added = result.importedCount ?? result.processed ?? 0;
      if (added === 0) {
        setSyncToast('No new loadboard emails found. Searched for "Elkhart Load Board" since Feb 15.');
        setTimeout(() => setSyncToast(null), 6000);
      }
      await new Promise((r) => setTimeout(r, 2000));
      await fetchData(Date.now());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const latestRun = runs[0];
  const latestItems = latestRun ? runItems[latestRun.id] || [] : [];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-2">
            <Truck className="w-8 h-8 text-amber-400" />
            Loadboard
          </h1>
          <p className="text-white/60 mt-1">Closest load options near your next drop-off</p>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {syncToast && (
        <div className="flex items-center gap-2 p-4 bg-amber-500/20 border border-amber-500/50 rounded-xl text-amber-200">
          <span>{syncToast}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-10 h-10 text-amber-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Current Drop-Off: human-readable city/state from latest settlement or BOL */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-amber-400" />
              Current Drop-Off (Latest Settlement / BOL)
            </h2>
            {lastKnownLocation?.city || lastKnownLocation?.state ? (
              <p className="text-xl font-medium text-white">
                {[sanitizeDisplay(lastKnownLocation.city), sanitizeDisplay(lastKnownLocation.state)].filter(Boolean).join(', ') || '—'}
              </p>
            ) : bols.length === 0 ? (
              <p className="text-white/60">No recent BOLs or settlement destination. Add BOLs or sync settlements.</p>
            ) : (
              <div className="space-y-4">
                {bols.map((bol) => {
                  const city = bol.bol?.to_city != null ? sanitizeDisplay(bol.bol.to_city) : '';
                  const state = bol.bol?.to_state != null ? sanitizeDisplay(bol.bol.to_state) : '';
                  const validCity = looksLikeValidCity(city);
                  const validState = /^[A-Z]{2}$/.test((state || '').trim());
                  const humanReadable = validCity && validState ? [city, state].filter(Boolean).join(', ') : '';
                  return (
                    <div key={bol.id} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-white">{sanitizeDisplay(bol.filename)}</p>
                          <p className="text-sm text-white/60">{new Date(bol.date).toLocaleDateString()}</p>
                        </div>
                        {bol.fileBacked && (bol.documentId || bol.downloadUrl) && (
                          <button
                            type="button"
                            onClick={async () => {
                              if (!bol.documentId) return;
                              try {
                                await downloadDocument(bol.documentId);
                              } catch (e) {
                                setError(e instanceof Error ? e.message : 'Download failed');
                              }
                            }}
                            className="flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm bg-transparent border-0 cursor-pointer p-0"
                          >
                            <ExternalLink className="w-4 h-4" />
                            View file
                          </button>
                        )}
                      </div>
                      {humanReadable ? (
                        <p className="mt-3 font-medium text-white/90">{humanReadable}</p>
                      ) : (
                        <p className="mt-3 text-amber-300 text-sm">Destination not extracted (raw file only)</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Latest Loadboard Run */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-amber-400" />
              Latest Loadboard Run
            </h2>
            {runs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-white/60 mb-4">No loadboard runs yet. Sync .Loadboard emails to populate.</p>
                <a
                  href={getGoogleAuthStartUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mr-3 text-amber-400 hover:text-amber-300 text-sm"
                >
                  Sign in with Google
                </a>
                <button
                  onClick={handleSyncLoadboard}
                  disabled={syncing}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/80 hover:bg-amber-500 text-shuttle-dark font-medium rounded-lg disabled:opacity-50"
                >
                  {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Sync .Loadboard (30 days)
                </button>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-white/70 text-sm">
                    {latestRun?.filename || 'Latest run'} · {latestItems.length} items
                  </p>
                  {(latestRun?.documentId || latestRun?.downloadUrl) && (
                    <div className="flex items-center gap-3 text-xs text-white/60">
                      {latestRun?.receivedAt && (() => {
                        const receivedDate = new Date(latestRun.receivedAt);
                        const isOlderThan48h = (Date.now() - receivedDate.getTime()) > 48 * 60 * 60 * 1000;
                        return (
                          <span className={isOlderThan48h ? 'font-bold text-red-400' : ''}>
                            Email received:{' '}
                            {receivedDate.toLocaleDateString()}
                          </span>
                        );
                      })()}
                      <button
                        type="button"
                        onClick={async () => {
                          if (!latestRun?.documentId) return;
                          try {
                            await downloadDocument(latestRun.documentId);
                          } catch (e) {
                            setError(e instanceof Error ? e.message : 'Download failed');
                          }
                        }}
                        className="flex items-center gap-1 text-amber-400 hover:text-amber-300 text-sm bg-transparent border-0 cursor-pointer p-0"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View attachment
                      </button>
                    </div>
                  )}
                </div>
                {latestItems.length > 0 ? (
                  <>
                    {needsOcrResync(latestItems) && (
                      <div className="mb-4 flex flex-wrap items-center gap-2">
                        <span className="text-amber-300 text-sm">Encoding issues or pending OCR. Re-sync to run visual-first extraction.</span>
                        <button
                          type="button"
                          onClick={handleSyncLoadboard}
                          disabled={syncing}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/80 hover:bg-amber-500 text-shuttle-dark text-sm font-medium rounded-lg disabled:opacity-50"
                          title="Re-fetch .Loadboard emails and re-parse with LOADS IN ELKHART / LOADS ON THE ROAD targeting"
                        >
                          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                          Re-sync with OCR
                        </button>
                      </div>
                    )}
                    <div className="overflow-x-auto rounded-lg border border-white/20">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-white/5 text-left text-white/80">
                            <th className="p-3 w-20">Unit</th>
                            <th className="p-3 min-w-[180px]">Pickup</th>
                            <th className="p-3 min-w-[180px]">Drop</th>
                            <th className="p-3 w-16">Miles</th>
                            <th className="p-3">Notes</th>
                          </tr>
                        </thead>
                        <tbody>
                          {latestItems.slice(0, 20).map((item) => {
                            const pickup = [item.pickup_city, item.pickup_state].filter(Boolean).join(', ');
                            const drop = [item.drop_city, item.drop_state].filter(Boolean).join(', ');
                            return (
                              <tr key={item.id} className="border-t border-white/10">
                                <td className="p-3 text-white/80 whitespace-nowrap">{sanitizeDisplay(item.unit_type) || '—'}</td>
                                <td className="p-3 text-white min-w-[180px] whitespace-nowrap overflow-hidden text-ellipsis" title={pickup || item.raw_row || ''}>{pickup ? sanitizeDisplay(pickup) : (item.raw_row ? sanitizeDisplay(item.raw_row.substring(0, 50)) : item.pickup_city === PENDING_OCR_CLEANUP ? PENDING_OCR_CLEANUP : '—')}</td>
                                <td className="p-3 text-white min-w-[180px] whitespace-nowrap overflow-hidden text-ellipsis" title={drop || item.raw_row || ''}>{drop ? sanitizeDisplay(drop) : (item.raw_row ? sanitizeDisplay(item.raw_row.substring(0, 50)) : item.drop_city === PENDING_OCR_CLEANUP ? PENDING_OCR_CLEANUP : '—')}</td>
                                <td className="p-3 text-white/80 whitespace-nowrap">{item.miles != null ? item.miles : '—'}</td>
                                <td className="p-3 text-white/70 truncate max-w-[200px]">{sanitizeDisplay(item.notes) || (item.raw_row ? sanitizeDisplay(item.raw_row) : '—')}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <p className="text-white/60">No parsed items (PDF may need OCR for image loadboards)</p>
                )}
              </>
            )}
          </div>

          {/* Recommended Loads */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
              <Truck className="w-5 h-5 text-amber-400" />
              Recommended Loads After Drop
            </h2>
            {destinations.length === 0 ? (
              <p className="text-white/60">Extract BOL destinations above to see recommendations.</p>
            ) : recommendations.length === 0 ? (
              <p className="text-white/60">No matching loads near {lastKnownLocation ? [lastKnownLocation.city, lastKnownLocation.state].filter(Boolean).join(', ') : destinations.map((d) => `${d.to_city || ''} ${d.to_state || ''}`).join(', ')}. Try syncing more loadboard data.</p>
            ) : (
              <div className="space-y-3">
                {lastKnownLocation && (
                  <p className="text-amber-200 text-sm">
                    Sorted by proximity to drop-off: {[lastKnownLocation.city, lastKnownLocation.state].filter(Boolean).join(', ')}
                  </p>
                )}
                <div className="overflow-x-auto rounded-lg border border-white/20">
                  <table className="w-full text-sm min-w-[520px]">
                    <thead>
                      <tr className="bg-white/5 text-left text-white/80">
                        <th className="p-3 min-w-[220px]">Pickup → Drop</th>
                        <th className="p-3 w-16">Miles</th>
                        <th className="p-3 min-w-[88px] whitespace-nowrap">Distance</th>
                        <th className="p-3">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recommendations.slice(0, 10).map((r) => {
                        const pickup = [r.pickup_city, r.pickup_state].filter(Boolean).join(', ');
                        const drop = [r.drop_city, r.drop_state].filter(Boolean).join(', ');
                        const within100 = r.distance_miles != null && r.distance_miles <= 100;
                        return (
                          <tr
                            key={r.id}
                            className={`border-t border-white/10 ${within100 ? 'bg-emerald-500/15' : ''}`}
                          >
                            <td className="p-3 text-white min-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis">
                              {pickup ? sanitizeDisplay(pickup) : '—'}
                              {' → '}
                              {drop ? sanitizeDisplay(drop) : (r.raw_row ? sanitizeDisplay(r.raw_row) : '—')}
                            </td>
                            <td className="p-3 text-white/80 whitespace-nowrap">{r.miles != null ? r.miles : '—'}</td>
                            <td className="p-3 min-w-[88px] whitespace-nowrap">
                              {r.distance_miles != null ? (
                                <span className={within100 ? 'text-emerald-300 font-medium' : 'text-white/70'}>
                                  {r.distance_miles} mi
                                  {within100 && ' ✓'}
                                </span>
                              ) : '—'}
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-0.5 rounded text-xs ${
                                within100 ? 'bg-emerald-500/30 text-emerald-200' :
                                r.reason.includes('City') ? 'bg-emerald-500/30 text-emerald-200' :
                                r.reason.includes('Same') ? 'bg-blue-500/30 text-blue-200' :
                                'bg-amber-500/30 text-amber-200'
                              }`}>
                                {r.reason}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
